import {
  CheckCircleFilled,
  CloseCircleFilled,
  CustomerServiceOutlined,
  LockOutlined,
  MailOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { Alert, Button, Form, Input, Progress, Result, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';

const PASSWORD_RULES = [
  { label: 'Minimum 8 characters', test: (value) => value.length >= 8 },
  { label: 'At least 1 uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { label: 'At least 1 lowercase letter', test: (value) => /[a-z]/.test(value) },
  { label: 'At least 1 number', test: (value) => /[0-9]/.test(value) },
  { label: 'At least 1 special character', test: (value) => /[^A-Za-z0-9]/.test(value) },
];

export default function AccountActivationStep({ email, onActivated, onFirstSignIn, onResend, onSignIn, planLabel, tokenStatus = 'VALID' }) {
  const [activated, setActivated] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [form] = Form.useForm();
  const password = Form.useWatch('password', form) || '';
  const completedRules = PASSWORD_RULES.filter(({ test }) => test(password)).length;
  const strength = completedRules * 20;
  const passwordValid = completedRules === PASSWORD_RULES.length;

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timerId = window.setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timerId);
  }, [resendCooldown]);

  const invalidState = useMemo(() => ({
    INVALID: { icon: <CloseCircleFilled />, title: 'Activation link is invalid', description: 'This activation link is not valid. Request a new email or contact support.' },
    EXPIRED: { icon: <WarningFilled />, title: 'Activation link has expired', description: 'For your security, activation links expire after a limited time. Request a new link to continue.' },
    ALREADY_USED: { icon: <CheckCircleFilled />, title: 'Account already activated', description: 'This activation link has already been used. You can sign in with your existing password.' },
  })[tokenStatus], [tokenStatus]);

  const resend = () => {
    onResend?.();
    setResendCooldown(60);
  };

  if (invalidState) {
    const alreadyUsed = tokenStatus === 'ALREADY_USED';
    return (
      <Result
        status={alreadyUsed ? 'success' : tokenStatus === 'INVALID' ? 'error' : 'warning'}
        icon={invalidState.icon}
        title={invalidState.title}
        subTitle={<>{invalidState.description}{email ? <><br />Account: <strong>{email}</strong></> : null}</>}
        extra={[
          !alreadyUsed ? <Button key="resend" type="primary" icon={<ReloadOutlined />} onClick={resend} disabled={resendCooldown > 0}>{resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend activation email'}</Button> : null,
          <Button key="signin" onClick={onSignIn}>{alreadyUsed ? 'Go to sign in' : 'Back to sign in'}</Button>,
          !alreadyUsed ? <Button key="support" icon={<CustomerServiceOutlined />} href="mailto:support@worksplaniq.com">Contact support</Button> : null,
        ].filter(Boolean)}
      />
    );
  }

  if (activated) {
    return <Result status="success" title="Your account is ready" subTitle="Your administrator account has been activated successfully." extra={<Button type="primary" onClick={onFirstSignIn}>Go to sign in</Button>} />;
  }

  const activate = () => {
    if (!passwordValid) return;
    onActivated?.();
    setActivated(true);
  };

  return (
    <section className="onboarding-activation">
      <span className="onboarding-activation-icon"><LockOutlined /></span>
      <Typography.Text className="onboarding-eyebrow">ACCOUNT ACTIVATION</Typography.Text>
      <Typography.Title level={2}>Create your password</Typography.Title>
      <Typography.Paragraph type="secondary">Secure your Organization Administrator account to continue to sign in.</Typography.Paragraph>
      <Alert showIcon icon={<SafetyCertificateOutlined />} type="success" message="Activation link verified" description={<>This link is ready for <strong>{email}</strong>.{planLabel ? <> Your selected plan is <strong>{planLabel}</strong>.</> : null}</>} />
      <Form form={form} layout="vertical" onFinish={activate}>
        <Form.Item label="Work Email"><Input prefix={<MailOutlined />} value={email} readOnly /></Form.Item>
        <Form.Item label="New Password" name="password" rules={[{ required: true, message: 'Enter a new password.' }]}><Input.Password prefix={<LockOutlined />} autoComplete="new-password" /></Form.Item>
        <div className="onboarding-password-strength"><Progress percent={strength} showInfo={false} status={strength < 60 ? 'exception' : strength < 100 ? 'normal' : 'success'} /><small>{strength < 60 ? 'Weak' : strength < 100 ? 'Good' : 'Strong'}</small></div>
        <div className="onboarding-password-rules">
          {PASSWORD_RULES.map((rule) => <div className={rule.test(password) ? 'is-complete' : ''} key={rule.label}>{rule.test(password) ? <CheckCircleFilled /> : <span />} {rule.label}</div>)}
        </div>
        <Form.Item label="Confirm Password" name="confirmPassword" dependencies={['password']} rules={[{ required: true, message: 'Confirm the password.' }, ({ getFieldValue }) => ({ validator(_, value) { return !value || getFieldValue('password') === value ? Promise.resolve() : Promise.reject(new Error('Passwords do not match.')); } })]}><Input.Password prefix={<LockOutlined />} autoComplete="new-password" /></Form.Item>
        {!passwordValid && password ? <Alert showIcon type="warning" message="Complete all password requirements before activating your account." /> : null}
        <Button type="primary" htmlType="submit" block disabled={!passwordValid}>Activate account</Button>
      </Form>
    </section>
  );
}
